import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import {
  HeatmapBucket,
  HeatmapBucketSortDirection,
  HeatmapBucketStatField,
  HeatmapBucketStats,
} from '../common/heatmap.helper';
import { HeatmapMetricDescriptor } from '../common/heatmap.types';
import InboxZero from '../components/InboxZero';
import { InputSearch } from '../components/inputs/InputSearch';
import { Container } from '../components/layout/ContainerFlex';
import { ActionBar } from '../components/nav/ActionBar';
import { HelpIndex, HelpNavTo } from '../helpContext/help.default';
import { HeatmapFilter } from '../simulation/store/simulation.types';
import HeatmapLegendBucket from './HeatmapLegendBucket';
import HeatmapLegendCharts from './HeatmapLegendCharts';
import HeatmapLegendPanelHeader, {
  HeatmapLegendPanelHeaderProps,
} from './HeatmapLegendPanelHeader';

export type HeatmapLegendPanelProps = {
  metricDescriptor: HeatmapMetricDescriptor<string, Record<string, any>>;
  buckets: HeatmapBucket[];
  hasStats: boolean;

  heatmapFilter: HeatmapFilter;
  onHeatmapFilterChange: (value: HeatmapFilter) => void;
  sortBy: HeatmapBucketStatField;
  onSortByChange: (value: HeatmapBucketStatField) => void;

  sortDirection: HeatmapBucketSortDirection;
  onSortDirectionChange: (value: HeatmapBucketSortDirection) => void;

  onBucketActionClick: (bucket: HeatmapBucket) => void;
  helpNavTo?: HelpNavTo<HelpIndex>;
};

const AGGREGATION_THRESHOLD = 30;
const TOP_ITEMS_LIMIT = 20;

// const aggregateSmallValues = (
//   items: ChartDataItem[],
//   threshold: number = AGGREGATION_THRESHOLD,
//   topLimit: number = TOP_ITEMS_LIMIT,
// ): ChartDataItem[] => {
//   if (items.length <= threshold) {
//     return items;
//   }

//   // Sort by value in descending order
//   const sortedItems = [...items].sort((a, b) => b.value - a.value);

//   // Take top N items
//   const topItems = sortedItems.slice(0, topLimit);

//   // Calculate the sum of remaining items
//   const remainingItems = sortedItems.slice(topLimit);
//   const remainingTotal = remainingItems.reduce(
//     (sum, item) => sum + item.value,
//     0,
//   );
//   const remainingRawTotal = remainingItems.reduce(
//     (sum, item) => sum + item.rawValue,
//     0,
//   );
//   const remainingPercentage = remainingItems.reduce(
//     (sum, item) => sum + item.percentage,
//     0,
//   );

//   // Add "Others" category if there are remaining items
//   if (remainingItems.length > 0) {
//     topItems.push({
//       id: 'Others',
//       value: remainingTotal,
//       color: TwTheme.extend.colors.chart.empty,
//       rawValue: remainingRawTotal,
//       percentage: remainingPercentage,
//       opacity: 1,
//     });
//   }

//   return topItems;
// };

const HeatmapLegendPanel: React.FC<HeatmapLegendPanelProps> = props => {
  const { t } = useTranslation('app');
  const { metricDescriptor, buckets } = props;
  const [showPercentage, setShowPercentage] = useState(false);
  const [showDistribution, setShowDistribution] = useState(false);
  const [filterText, setFilterText] = useState('');

  const debouncedClick = useCallback(
    _.debounce(
      (
        id: string,
        value: boolean,
        evt: React.MouseEvent<HTMLDivElement, MouseEvent>,
      ) => {
        if (evt.detail === 2) {
          forceSelectSingleBucket(id);
        } else {
          updateBucketFilter(id, value);
        }
      },
      400,
    ),
    [props.heatmapFilter, props.buckets],
  );

  const updateBucketFilter = (id: string, value: boolean) => {
    props.onHeatmapFilterChange({
      ...props.heatmapFilter,
      hiddenBuckets: {
        ...props.heatmapFilter?.hiddenBuckets,
        [id]: value,
      },
    });
  };

  const forceSelectSingleBucket = (id: string) => {
    const hiddenBuckets = _.reduce(
      props.buckets,
      (acc, b) => ({
        ...acc,
        [b.id]: b.id !== id,
      }),
      {},
    );

    props.onHeatmapFilterChange({
      ...props.heatmapFilter,
      hiddenBuckets,
    });
  };

  const allSelected = !_.some(props.heatmapFilter.hiddenBuckets);

  const toggleSelectAllBuckets = () => {
    const hiddenBuckets = _.reduce(
      buckets,
      (acc, b) => ({
        ...acc,
        [b.id]: allSelected,
      }),
      {},
    );

    props.onHeatmapFilterChange({
      ...props.heatmapFilter,
      hiddenBuckets,
    });
  };

  const hasSearch = buckets.length > 12;
  const shouldApplySearchFilter = hasSearch && !_.isEmpty(filterText);
  const bucketsCount = _.size(buckets);
  const title = `${metricDescriptor?.title ?? '—'} ${bucketsCount > 1 ? `(${bucketsCount})` : ''}`;
  const bucketsFiltered = _.filter(
    buckets,
    b =>
      !shouldApplySearchFilter ||
      b.title.toLowerCase().includes(filterText.toLowerCase()),
  );
  const filteredBucketsCount = _.size(bucketsFiltered);

  // Calculate total stats for percentage calculations
  const totalStats = bucketsFiltered.reduce(
    (acc, bucket) => ({
      linesCount: (acc.linesCount || 0) + (bucket.stats?.linesCount || 0),
      itemsCount: (acc.itemsCount || 0) + (bucket.stats?.itemsCount || 0),
      totalExistedLocations:
        (acc.totalExistedLocations || 0) +
        (bucket.stats?.totalExistedLocations || 0),
      totalCount: (acc.totalCount || 0) + (bucket.stats?.totalCount || 0),
      assignmentsCount:
        (acc.assignmentsCount || 0) + (bucket.stats?.assignmentsCount || 0),
      locationCount:
        (acc.locationCount || 0) + (bucket.stats?.locationCount || 0),
    }),
    {} as Record<keyof HeatmapBucketStats, number>,
  );

  const legendHeaderProps: HeatmapLegendPanelHeaderProps = {
    ...props,
    title,
    allSelected,
    toggleSelectAll: toggleSelectAllBuckets,
    showPercentage,
    onShowPercentageChange: setShowPercentage,
    showDistribution,
    onShowDistributionChange: setShowDistribution,
  };

  // Determine chart type based on metric descriptor
  const chartType =
    metricDescriptor?.type === 'categoryABC' ||
    metricDescriptor?.type === 'abc' ||
    metricDescriptor?.type === 'percentRank'
      ? 'abc'
      : 'bar';

  return (
    <Container data-component="HeatmapLegendPanel" col hasOverflowY>
      <HeatmapLegendPanelHeader {...legendHeaderProps} />
      {hasSearch && (
        <ActionBar sticky stickyTop className="px-2 py-1">
          <InputSearch
            value={filterText}
            onChange={value => setFilterText(value)}
            placeholder={t`Filter...`}
            className={'w-full'}
          />
        </ActionBar>
      )}
      {showDistribution && !_.isEmpty(bucketsFiltered) && (
        <HeatmapLegendCharts
          buckets={bucketsFiltered}
          field={props.sortBy}
          metricDescriptor={props.metricDescriptor}
          showPercentage={showPercentage}
          totalStats={totalStats}
          heatmapFilter={props.heatmapFilter}
          chartType={chartType}
        />
      )}
      {_.isEmpty(bucketsFiltered) ? (
        <InboxZero selfCenter hasIcon>
          {filterText ? t`No matching buckets` : t`No Items`}
        </InboxZero>
      ) : (
        <Virtuoso
          className={'h-full'}
          data={bucketsFiltered}
          itemContent={(index, bucket) => {
            const isSelected =
              allSelected ||
              props.heatmapFilter.hiddenBuckets[bucket.id] !== true;

            return (
              <HeatmapLegendBucket
                key={`legend-stat-bucket-${bucket.id}`}
                metricDescriptor={metricDescriptor}
                bucket={bucket}
                hasStats={props.hasStats}
                dataField={props.sortBy}
                isSelected={isSelected}
                showPercentage={showPercentage}
                totalStats={totalStats}
                onClick={evt => debouncedClick(bucket.id, isSelected, evt)}
                onBucketActionClick={() => props.onBucketActionClick(bucket)}
                className="mb-0.5"
              />
            );
          }}
        />
      )}
    </Container>
  );
};

export default HeatmapLegendPanel;
